import { useCallback, useMemo, useState } from 'react'
import { STRIPE_PUBLIC_KEY } from 'Config/constants'
import {
  useStripe, Elements, useElements, PaymentElement,
} from '@stripe/react-stripe-js'
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js'
import Button from '@components/Button'


export type StripePaymentElementProps = {
  options: StripeElementsOptions
  stripeAccount: string | null
  returnUrl: string
  onPaymentSuccess: (param: { paymentIntentId: string }) => void
  getClientSecret: () => Promise<{ clientSecret: string, customerId: string, subscriptionId: string, idStripeAccount: string, error: any }>
  close?: () => void
  onError: (param?: { message?: string, customerId?: string, idStripeAccount?: string, subscriptionId?: string }) => void
  hiddenButton?: boolean
  buttonVariant?: 'primary' | 'secondary' | 'link' | 'black'
}


const StripePaymentElement = ({
  options, stripeAccount, onPaymentSuccess, returnUrl, onError, getClientSecret, hiddenButton, close, buttonVariant
}: StripePaymentElementProps) => {
  const stripePromise = useMemo(() => loadStripe(
    STRIPE_PUBLIC_KEY,
    {
      locale: 'fr-FR',
      ...(stripeAccount ? { stripeAccount } : {}),
    },
  ), [stripeAccount])

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripePaymentElementForm
        onPaymentSuccess={onPaymentSuccess}
        getClientSecret={getClientSecret}
        onError={onError}
        returnUrl={returnUrl}
        hiddenButton={hiddenButton}
        close={close}
        buttonVariant={buttonVariant}
      />
    </Elements>
  )
}

const StripePaymentElementForm = ({
  onPaymentSuccess, returnUrl, onError, getClientSecret, hiddenButton, close, buttonVariant
}: Pick<StripePaymentElementProps, 'onPaymentSuccess' | 'returnUrl' | 'onError' | 'getClientSecret' | 'hiddenButton' | 'close' | 'buttonVariant'>) => {
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [isSubmitting, setSubmitting] = useState(false)

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      if (!stripe || !elements || !onPaymentSuccess) return

      const { clientSecret, customerId, subscriptionId, idStripeAccount, error: clientSecretError } = await getClientSecret()
      const { error: submitError } = await elements.submit()
      console.log('clientSecretError --------------------------------->')
      console.log(clientSecretError)
      console.log('submitError --------------------------------->')
      console.log(submitError)
      
      if (clientSecretError) return onError({ message: 'Une erreur est survenue.'})
      if (submitError) return onError({ message: submitError.message || 'Une erreur est survenue.' })

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
      })

      console.log('paymentIntent --------------------------------->')
      console.log(paymentIntent)
      console.log('error --------------------------------->')
      console.log(error)

      let errorMessage = error?.message
      if (error?.code === 'payment_intent_authentication_failure') {
        errorMessage = `${errorMessage} Si le problème persiste, tentez de désactiver votre bloqueur de publicité ou d'utiliser un autre navigateur.`
      }

      if (error) {
        setErrorMessage(errorMessage)
        onError({ message: errorMessage, customerId, subscriptionId, idStripeAccount })
      } else {
        onPaymentSuccess({ paymentIntentId: paymentIntent.id })
        if (close) close()
      }
    } catch (e) {
      console.log('handleSubmit error --------------------------------->')
      console.log(e)
      setErrorMessage('Une erreur est survenue lors du paiement, veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }, [stripe, elements, getClientSecret, onError, onPaymentSuccess, returnUrl, close])

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          defaultValues: {
            card: {
              network: ['visa', 'mastercard', 'cartes_bancaires'],
            },
          },
          terms: {
            card: 'never',
          },
        }}
      />
      {hiddenButton ? (
        <input type="submit" id="submit-cb-form" style={{ display: 'none' }} />
      ) : (
        <Button
          isDisabled={!stripe}
          type="submit"
          isLoading={isSubmitting}
          className="mt-4"
          variant={buttonVariant || 'primary'}
        >
          <label
            htmlFor="submit-cb-form"
            tabIndex={0}
          >
            PAIEMENT
          </label>
        </Button>
      )}
      {errorMessage && <div className="mt-4 text-pastel-red">{errorMessage}</div>}
    </form>
  )
}

export default StripePaymentElement